import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import reading.CompactOption
import reading.Task2Layout
import reading.Task3Layout
import reading.Task4Layout
import reading.Task5Layout

@Composable
fun ExamScreen(onExit: () -> Unit) {
    // --- STATE ---
    var examData by remember { mutableStateOf<Map<Int, List<ReadingQuestion>>>(emptyMap()) }
    var isLoading by remember { mutableStateOf(true) }

    // Состояние экзамена
    var currentTaskIndex by remember { mutableStateOf(0) } // 0..4
    var timeLeftSeconds by remember { mutableStateOf(60 * 60) } // 60 минут
    var isExamFinished by remember { mutableStateOf(false) }

    // Ответы пользователя (Глобальные для всего экзамена)
    // Ключ: ID вопроса, Значение: Ответ ("A", "B"...)
    var globalAnswers by remember { mutableStateOf<Map<String, String>>(emptyMap()) }

    // --- ЗАГРУЗКА ---
    LaunchedEffect(Unit) {
        examData = DataRepository.fetchExamSet()
        isLoading = false
    }

    // --- ТАЙМЕР ---
    LaunchedEffect(isLoading, isExamFinished) {
        if (!isLoading && !isExamFinished) {
            while (timeLeftSeconds > 0) {
                delay(1000L)
                timeLeftSeconds--
            }
            if (timeLeftSeconds == 0) {
                isExamFinished = true // Время вышло
            }
        }
    }

    // --- UI ---
    Div({
        style {
            width(100.percent)
            minHeight(100.vh)
            backgroundColor(Color("#f4f6f7"))
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        // 1. ЗАГРУЗКА
        if (isLoading) {
            Div({ style { padding(50.px); textAlign("center") } }) {
                H2 { Text("🔄 Preparando el examen...") }
                P { Text("Por favor, espere.") }
            }
            return@Div
        }

        // 2. ЕСЛИ ДАННЫХ НЕТ
        if (examData.isEmpty()) {
            Div({ style { padding(50.px); textAlign("center") } }) {
                H2 { Text("⚠️ Error de configuración") }
                P { Text("No se encontraron preguntas de examen en la base de datos.") }
                Button({ onClick { onExit() } }) { Text("Salir") }
            }
            return@Div
        }

        // 3. ЭКРАН РЕЗУЛЬТАТОВ (ПОСЛЕ ЗАВЕРШЕНИЯ)
        if (isExamFinished) {
            ExamResultView(examData, globalAnswers, onExit)
            return@Div
        }

        // 4. ОСНОВНОЙ ЭКРАН ЭКЗАМЕНА

        // --- ВЕРХНЯЯ ПАНЕЛЬ (ТАЙМЕР) ---
        Div({
            style {
                backgroundColor(Color("#2c3e50"))
                color(Color.white)
                padding(15.px, 30.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                position(Position.Sticky)
                top(0.px)
                property("z-index", "100")
                property("box-shadow", "0 2px 10px rgba(0,0,0,0.2)")
            }
        }) {
            // Лево: Задача X / 5
            H3({ style { margin(0.px) } }) {
                Text("Tarea ${currentTaskIndex + 1} de 6")
            }

            // Право: Таймер
            val minutes = timeLeftSeconds / 60
            val seconds = timeLeftSeconds % 60
            val colorTimer = if (minutes < 5) Color("#e74c3c") else Color.white // Красный если мало времени

            Div({ style { fontSize(1.5.cssRem); fontWeight("bold"); color(colorTimer) } }) {
                Text("${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}")
            }
        }

        // --- ТЕЛО ЗАДАЧИ ---
        val currentTaskId = currentTaskIndex + 1 // 1..6
        val currentQuestions = examData[currentTaskId] ?: emptyList()

        Div({
            style {
                flex(1)
                padding(20.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
            }
        }) {
            // Отображаем соответствующий лейаут задачи
            // Важно: мы переиспользуем существующие компоненты, но нам нужно скрыть их кнопки "Finalizar"
            // Мы это сделаем, просто не передавая им callback onFinish, или игнорируя его,
            // а свою кнопку навигации добавим ниже.

            when (currentTaskId) {
                1 -> Task1WizardExamMode(
                    questions = currentQuestions,
                    answers = globalAnswers,
                    onAnswer = { qId, ans -> globalAnswers = globalAnswers + (qId to ans) }
                )

                2, 3, 4, 5, 6 -> GenericExamTaskLayout(
                    taskId = currentTaskId,
                    questions = currentQuestions,
                    answers = globalAnswers,
                    onAnswer = { qId, ans -> globalAnswers = globalAnswers + (qId to ans) }
                )
            }

            // --- КНОПКА НАВИГАЦИИ (СЛЕДУЮЩАЯ ЗАДАЧА) ---
            Div({
                style {
                    width(100.percent)
                    maxWidth(1200.px)
                    marginTop(30.px)
                    marginBottom(50.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                }
            }) {
                Button({
                    style {
                        padding(15.px, 40.px)
                        backgroundColor(Color("#2980b9"))
                        color(Color.white)
                        border(0.px)
                        borderRadius(8.px)
                        fontSize(1.2.cssRem)
                        cursor("pointer")
                        fontWeight("bold")
                    }
                    onClick {
                        if (currentTaskIndex < 5) {
                            currentTaskIndex++
                            // Скролл вверх при переходе
                            kotlinx.browser.window.scrollTo(0.0, 0.0)
                        } else {
                            // Это была последняя задача
                            isExamFinished = true
                        }
                    }
                }) {
                    Text(if (currentTaskIndex == 5) "FINALIZAR EXAMEN" else "Siguiente Tarea ➜")
                }
            }
        }
    }
}

// --- ВСПОМОГАТЕЛЬНЫЕ КОМПОНЕНТЫ ДЛЯ ЭКЗАМЕНА ---

@Composable
fun Task1WizardExamMode(
    questions: List<ReadingQuestion>,
    answers: Map<String, String>,
    onAnswer: (String, String) -> Unit
) {
    // В экзамене Tarea 1 лучше показывать списком, чтобы было быстрее,
    // или использовать Wizard, но без кнопки "Finish". Сделаем список для обзорности.
    Div({ style { maxWidth(800.px); width(100.percent) } }) {
        questions.forEachIndexed { index, q ->
            Div({
                style {
                    backgroundColor(Color.white); padding(20.px); borderRadius(10.px)
                    marginBottom(20.px); property("box-shadow", "0 2px 5px rgba(0,0,0,0.05)")
                }
            }) {
                H4 { Text("Pregunta ${index + 1}") }
                P({ style { fontStyle("italic"); backgroundColor(Color("#f9f9f9")); padding(10.px) } }) { Text(q.textContent) }
                P({ style { fontWeight("bold") } }) { Text(q.question) }

                // Варианты
                listOf("A" to q.optionA, "B" to q.optionB, "C" to q.optionC).forEach { (key, text) ->
                    val isSelected = answers[q.id] == key
                    Div({
                        onClick { onAnswer(q.id, key) }
                        style {
                            padding(10.px); marginTop(5.px); cursor("pointer"); borderRadius(5.px)
                            border(1.px, LineStyle.Solid, if (isSelected) Color("#3498db") else Color("#ecf0f1"))
                            backgroundColor(if (isSelected) Color("#ebf5fb") else Color.white)
                        }
                    }) { Text("$key. $text") }
                }
            }
        }
    }
}

@Composable
fun GenericExamTaskLayout(
    taskId: Int,
    questions: List<ReadingQuestion>,
    answers: Map<String, String>,
    onAnswer: (String, String) -> Unit
) {
    // Здесь мы просто переиспользуем существующие лейауты из PracticeScreen.kt
    // Но так как они требуют onFinish, мы передаем пустую лямбду {},
    // потому что кнопку финиша мы рисуем сами в ExamScreen.

    when (taskId) {
        2 -> Task2Layout(questions, answers, onAnswer, onFinish = {})
        3 -> Task3Layout(questions, answers, onAnswer, onFinish = {})
        4 -> Task4Layout(questions, answers, onAnswer, onFinish = {}) // Первая Tarea 4
        5 -> Task4Layout(questions, answers, onAnswer, onFinish = {}) // Вторая Tarea 4
        6 -> Task5Layout(questions, answers, onAnswer, onFinish = {})
    }
}

@Composable
fun ExamResultView(
    examData: Map<Int, List<ReadingQuestion>>,
    userAnswers: Map<String, String>,
    onExit: () -> Unit
) {
    // --- ПОДСЧЕТ БАЛЛОВ ---
    var totalCorrect = 0
    var totalQuestions = 0
    // Подсчет по задачам для отображения
    val taskResults = examData.map { (step, questions) ->
        val score = questions.count { q -> userAnswers[q.id] == q.correctOption }
        totalCorrect += score
        Triple(step, score, questions.size)
    }
/*
    examData.forEach { (step, questions) ->
        val score = questions.count { q -> userAnswers[q.id] == q.correctOption }
        totalCorrect += score
        totalQuestions += questions.size
        taskResults.add(Triple(step, score, questions.size))
    }

 */

// --- НОВАЯ ШКАЛА SIELE (0-38 вопросов) ---
    val sieleLevel = when (totalCorrect) {
        in 33..38 -> "C1 (Dominio Operativo)"
        in 27..32 -> "B2 (Avanzado)"
        in 18..26 -> "B1 (Umbral)"
        in 10..17 -> "A2 (Plataforma)"
        in 5..9 -> "A1 (Acceso)"
        else -> "Inferior a A1"
    }

    // Расчет баллов (Линейная интерполяция по диапазонам SIELE)
    // 0-250 шкала.
    val sielePoints = when (totalCorrect) {
        in 33..38 -> 217 + ((totalCorrect - 33.0) / (38 - 33) * (250 - 217))
        in 27..32 -> 178 + ((totalCorrect - 27.0) / (32 - 27) * (216.99 - 178))
        in 18..26 -> 118 + ((totalCorrect - 18.0) / (26 - 18) * (177.99 - 118))
        in 10..17 -> 66 + ((totalCorrect - 10.0) / (17 - 10) * (117.99 - 66))
        in 5..9 -> 33 + ((totalCorrect - 5.0) / (9 - 5) * (65.99 - 33))
        else -> 0 + (totalCorrect.toDouble() / 4.0 * 32.99)
    }.toInt()

    val sieleColor = when {
        totalCorrect >= 26 -> "#27ae60" // Green
        totalCorrect >= 18 -> "#f39c12" // Orange
        else -> "#c0392b" // Red
    }

    Div({
        style {
            padding(40.px); maxWidth(800.px); property("margin", "0 auto")
            backgroundColor(Color.white); marginTop(20.px); borderRadius(12.px)
            textAlign("center"); property("box-shadow", "0 10px 30px rgba(0,0,0,0.1)")
        }
    }) {
        H1 { Text("🏁 Examen Finalizado") }

        Div({
            style {
                backgroundColor(Color(sieleColor)); color(Color.white)
                padding(30.px); borderRadius(10.px); marginTop(20.px); marginBottom(30.px)
            }
        }) {
            H2({ style { margin(0.px); fontSize(3.cssRem) } }) { Text(sieleLevel) }
            P({ style { fontSize(1.5.cssRem); marginTop(10.px) } }) { Text("$sielePoints / 250 puntos") }
            P { Text("Aciertos: $totalCorrect / $totalQuestions") }
        }

        H3({ style { textAlign("left"); marginBottom(15.px) } }) { Text("Desglose por Tarea:") }

        taskResults.forEach { (step, score, total) ->
            val label = when (step) {
                4 -> "Tarea 4 (Parte 1)"
                5 -> "Tarea 4 (Parte 2)"
                6 -> "Tarea 5"
                else -> "Tarea $step"
            }
            Div({
                style {
                    display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                    padding(10.px); property("border-bottom", "1px solid #ecf0f1")
                }
            }) {
                Span { Text(label) }
                Span({ style { fontWeight("bold") } }) { Text("$score / $total") }
            }
        }

               // СОХРАНЕНИЕ
        LaunchedEffect(Unit) {
            console.log(">>> Начинаем сохранение результатов экзамена...")

            // 1. Сохраняем общий результат
            try {
                DataRepository.saveAttempt(
                    taskId = 999,
                    setTitle = "Simulacro SIELE Global",
                    score = totalCorrect,
                    total = totalQuestions,
                    durationSeconds = 3600,
                    details = emptyList()
                )
            } catch (e: Exception) {
                console.error("Ошибка сохранения Attempt: ${e.message}")
            }

            // 2. Помечаем сеты как решенные
            examData.forEach { (stepIndex, questions) ->
                val taskSetId = questions.firstOrNull()?.setId
                if (taskSetId != null) {
                    val score = questions.count { q -> userAnswers[q.id] == q.correctOption }

                    // Определяем тип задачи (1..5)
                    val realTaskType = when (stepIndex) {
                        4, 5 -> 4
                        6 -> 5
                        else -> stepIndex
                    }

                    // Сохраняем прогресс (Оборачиваем в try-catch, чтобы видеть ошибки в консоли браузера)
                    try {
                        DataRepository.saveProgress(
                            taskId = realTaskType,
                            setId = taskSetId,
                            score = score,
                            total = questions.size
                        )
                        console.log(">>> Сет $taskSetId (Tarea $realTaskType) сохранен.")
                    } catch (e: Exception) {
                        console.error(">>> ОШИБКА сохранения сета $taskSetId: ${e.message}")
                    }
                }
            }
        }

        Button({
            style {
                marginTop(30.px); padding(15.px, 30.px)
                backgroundColor(Color("#34495e")); color(Color.white)
                border(0.px); borderRadius(8.px); cursor("pointer")
            }
            onClick { onExit() }
        }) { Text("Volver al Menú Principal") }
    }
}

@Composable
fun Task2LayoutEx(
    questions: List<ReadingQuestion>,
    userAnswers: Map<String, String>,
    onAnswer: (String, String) -> Unit,
    onFinish: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            property("gap", "30px")
            width(100.percent)
            maxWidth(1200.px)
            // На мобильных переносим колонки друг под друга
            property("flex-wrap", "wrap")
            alignItems(AlignItems.FlexStart) // Прижать к верху
        }
    }) {
        // --- ЛЕВАЯ КОЛОНКА: ТЕКСТ ---
        Div({
            style {
                flex(1) // Занимает 50% места
                minWidth(300.px)
                backgroundColor(Color.white)
                padding(30.px)
                borderRadius(12.px)
                property("box-shadow", "0 4px 15px rgba(0,0,0,0.05)")
                // Делаем текст "липким" при скролле (удобно на десктопе)
                property("position", "sticky")
                property("top", "20px")
                property("height", "fit-content")
            }
        }) {
            H3({ style { marginBottom(20.px); color(Color("#2c3e50")) } }) {
                Text("Lea el texto y responda:")
            }

            // Собираем текст из всех вопросов в один длинный рассказ

                P({
                    style {
                        lineHeight("1.6em")
                        fontSize(1.1.cssRem)
                        marginBottom(15.px)
                        color(Color("#34495e"))
                        textAlign("justify")
                    }
                }) {
                    Text(questions[0].textContent)
                }

        }

        // --- ПРАВАЯ КОЛОНКА: СПИСОК ВОПРОСОВ ---
        Div({
            style {
                flex(1) // Занимает 50% места
                minWidth(300.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("gap", "20px")
            }
        }) {
            questions.forEachIndexed { index, question ->
                Div({
                    style {
                        backgroundColor(Color.white)
                        padding(20.px)
                        borderRadius(12.px)
                        property("box-shadow", "0 2px 10px rgba(0,0,0,0.05)")
                    }
                }) {
                    // Номер вопроса
                    Div({
                        style {
                            fontSize(0.9.cssRem)
                            color(Color("#7f8c8d"))
                            marginBottom(10.px)
                            fontWeight("bold")
                        }
                    }) { Text("Pregunta ${index + 1}") }

                    // Сам вопрос
                    H4({ style { marginBottom(15.px); color(Color("#2c3e50")) } }) {
                        Text(question.question)
                    }

                    // Варианты (Компактные)
                    CompactOption("A", question.optionA, userAnswers[question.id] == "A") { onAnswer(question.id, "A") }
                    CompactOption("B", question.optionB, userAnswers[question.id] == "B") { onAnswer(question.id, "B") }
                    CompactOption("C", question.optionC, userAnswers[question.id] == "C") { onAnswer(question.id, "C") }
                }
            }

            // Кнопка завершения (внизу списка вопросов)
            Button({
                style {
                    padding(15.px)
                    backgroundColor(Color("#2ecc71"))
                    color(Color.white)
                    border(0.px)
                    borderRadius(8.px)
                    fontSize(1.2.cssRem)
                    cursor("pointer")
                    width(100.percent)
                    marginTop(20.px)
                }
                onClick { onFinish() }
            }) {
                // Показываем, сколько отвечено
                val answeredCount = userAnswers.keys.count { id -> questions.any { it.id == id } }
                Text(if (answeredCount == questions.size) "Finalizar Test" else "Finalizar ($answeredCount/${questions.size})")
            }
        }
    }
}
